import { useState, useCallback, memo, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditableSelect from '../EditableDisplayElements/EditableSelect';
import EditablePassword from '../EditableDisplayElements/EditablePassword';
import EditableText from '../EditableDisplayElements/EditableText';
import EditableBoolean from '../EditableDisplayElements/EditableBoolean';
import EditablePrice from '../EditableDisplayElements/EditablePrice';
import { validateForm, validateField } from '../../../util/validation';
import LockIconWrapper from '../../components/LockIconWrapper';

const AddItemDialog = memo(function AddItemDialog({
  open,
  onClose,
  onAdd,
  schema,
  apiOptions,
  title,
  resourceType = 'resource',
}) {
  const [newItem, setNewItem] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Responsive helpers
  const theme = useTheme();
  const isXsDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md')); // ≤900px

  const isFormValid = useMemo(() => {
    // check if there are any validation errors
    if (Object.keys(validationErrors).length > 0) {
      return false;
    }

    // check if at least one field has a value
    const hasAnyValue = Object.keys(newItem).some(key => {
      const value = newItem[key];
      return value && value !== '' && value !== null && value !== undefined;
    });

    return hasAnyValue;
  }, [validationErrors, newItem]);

  const handleNewItemChange = useCallback(
    (key, value) => {
      setNewItem(prev => ({ ...prev, [key]: value }));
      setHasUnsavedChanges(true);

      setValidationErrors(prev => {
        const newErrors = { ...prev };
        const fieldSchema = schema[key];

        if (fieldSchema) {
          const schemaForValidation =
            fieldSchema.type === 'price' || fieldSchema.type === 'milliseconds'
              ? { ...fieldSchema, type: 'number' }
              : fieldSchema;
          const validation = validateField(value, schemaForValidation);
          if (validation.isValid) {
            delete newErrors[key];
          } else {
            newErrors[key] = validation.errors;
          }
        }

        return newErrors;
      });
    },
    [schema]
  );

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      onClose();
      setNewItem({});
      setValidationErrors({});
    }
  }, [hasUnsavedChanges, onClose]);

  const handleConfirmClose = useCallback(() => {
    setShowConfirmDialog(false);
    onClose();
    setNewItem({});
    setValidationErrors({});
    setHasUnsavedChanges(false);
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const handleAdd = useCallback(() => {
    const hasAnyValue = Object.keys(newItem).some(key => {
      const value = newItem[key];
      return value && value !== '' && value !== null && value !== undefined;
    });

    if (!hasAnyValue) {
      return;
    }

    // Normalize schema for validation: treat price like number
    const normalizedSchema = Object.fromEntries(
      Object.entries(schema || {}).map(([k, s]) => [
        k,
        s?.type === 'price' || s?.type === 'milliseconds' ? { ...s, type: 'number' } : s,
      ])
    );
    const validation = validateForm(newItem, normalizedSchema);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    const passwordFields = Object.keys(schema).filter(key => schema[key].type === 'password');
    for (const key of passwordFields) {
      if (newItem[`${key}-password`] !== newItem[`${key}-confirm`]) {
        return;
      }
    }
    const preparedItem = { ...newItem };
    for (const key of passwordFields) {
      preparedItem[key] = newItem[`${key}-password`];
      delete preparedItem[`${key}-password`];
      delete preparedItem[`${key}-confirm`];
    }
    Object.keys(schema).forEach(key => {
      const fieldSchema = schema[key];
      if (!fieldSchema) return;
      const isSelect = fieldSchema.type === 'select' || fieldSchema.type === 'multi-select';
      if (!isSelect && fieldSchema.transformValue && preparedItem[key] !== undefined) {
        preparedItem[key] = fieldSchema.transformValue(preparedItem[key]);
      }
    });

    onAdd(preparedItem);
    onClose();
    setNewItem({});
    setValidationErrors({});
    setHasUnsavedChanges(false);
  }, [newItem, schema, onAdd, onClose]);

  const handleSubmit = useCallback(
    e => {
      // prevent default form submission behavior
      e?.preventDefault?.();
      handleAdd();
    },
    [handleAdd]
  );

  const renderField = useCallback(
    (key, fieldSchema) => {
      if (fieldSchema.hideInCreate || fieldSchema.excludeFromAdd) return null;

      // Conditional visibility: hide a field when its showIf predicate (evaluated
      // against the in-progress new item) returns false.
      if (typeof fieldSchema.showIf === 'function' && !fieldSchema.showIf(newItem)) return null;

      const placeholder = fieldSchema.placeholder || fieldSchema.label;
      const fieldErrors = validationErrors[key] || [];
      const hasError = fieldErrors.length > 0;

      const commonProps = {
        key,
        value: newItem[key],
        setValue: value => handleNewItemChange(key, value),
        isEditable: true,
        label: fieldSchema.label,
        placeholder,
        schema: fieldSchema,
        required: fieldSchema.required,
        showValidation: false,
      };

      if (fieldSchema.type === 'password') {
        return (
          <Box key={key}>
            <EditablePassword {...commonProps} />
            {hasError && (
              <Typography variant='caption' color='error' sx={{ mt: 0.5, display: 'block' }}>
                {fieldErrors[0]}
              </Typography>
            )}
          </Box>
        );
      }
      if (fieldSchema.type === 'select' || fieldSchema.type === 'multi-select') {
        let selectValue = newItem[key];
        if (fieldSchema.type === 'multi-select') {
          if (!Array.isArray(selectValue)) selectValue = [];
        } else {
          if (selectValue === undefined || selectValue === null) selectValue = '';
        }

        // Normalize dialog value to option primitive(s) so chips render even if transformValue
        // converted it to objects (e.g., { entertainmentId, name })
        const idName = fieldSchema.api?.idName;
        if (idName) {
          if (Array.isArray(selectValue)) {
            selectValue = selectValue.map(v => (v && typeof v === 'object' ? v[idName] : v));
          } else if (selectValue && typeof selectValue === 'object') {
            selectValue = selectValue[idName] ?? '';
          }
        }
        return (
          <Box key={key}>
            <EditableSelect
              {...commonProps}
              value={selectValue}
              type={fieldSchema.type}
              options={apiOptions[key] || fieldSchema.options || []}
              getOptions={fieldSchema.getOptions}
              parentItem={newItem}
              isDialog={true}
              isAddDialog={true}
              placeholder={placeholder}
              showDefaultAsDisplayValue={true}
              showValidation={false}
            />
            {hasError && (
              <Typography variant='caption' color='error' sx={{ mt: 0.5, display: 'block' }}>
                {fieldErrors[0]}
              </Typography>
            )}
          </Box>
        );
      }
      if (fieldSchema.type === 'price') {
        return (
          <Box key={key}>
            <EditablePrice {...commonProps} isDialog={true} showDefaultAsDisplayValue={true} />
            {hasError && (
              <Typography variant='caption' color='error' sx={{ mt: 0.5, display: 'block' }}>
                {fieldErrors[0]}
              </Typography>
            )}
          </Box>
        );
      }
      if (fieldSchema.type === 'boolean') {
        return (
          <Box key={key}>
            <EditableBoolean
              {...commonProps}
              isDialog={true}
              showLabel={true}
              label={fieldSchema.label}
            />
            {hasError && (
              <Typography variant='caption' color='error' sx={{ mt: 0.5, display: 'block' }}>
                {fieldErrors[0]}
              </Typography>
            )}
          </Box>
        );
      }

      if (fieldSchema.type === 'milliseconds') {
        const minutes =
          typeof commonProps.value === 'number'
            ? commonProps.value / 60000
            : commonProps.value === null || commonProps.value === undefined || commonProps.value === ''
              ? null
              : Number(commonProps.value) / 60000;

        const minutesValue = Number.isFinite(minutes) ? minutes : null;

        return (
          <Box key={key}>
            <EditableText
              {...commonProps}
              value={minutesValue}
              setValue={newValue => {
                if (newValue === '' || newValue === null || newValue === undefined) {
                  handleNewItemChange(key, null);
                  return;
                }
                const parsed = Number.parseFloat(String(newValue));
                if (!Number.isFinite(parsed)) return;
                handleNewItemChange(key, Math.round(parsed * 60000));
              }}
              type={'number'}
              schema={{ ...fieldSchema, type: 'number' }}
              displayValue={fieldSchema.displayValue}
              showDefaultAsDisplayValue={true}
              isDialog={true}
            />
            {hasError && (
              <Typography variant='caption' color='error' sx={{ mt: 0.5, display: 'block' }}>
                {fieldErrors[0]}
              </Typography>
            )}
          </Box>
        );
      }
      return (
        <Box key={key}>
          <EditableText
            {...commonProps}
            type={fieldSchema.type}
            displayValue={fieldSchema.displayValue}
            showDefaultAsDisplayValue={true}
            isDialog={true}
          />
          {hasError && (
            <Typography variant='caption' color='error' sx={{ mt: 0.5, display: 'block' }}>
              {fieldErrors[0]}
            </Typography>
          )}
        </Box>
      );
    },
    [newItem, apiOptions, handleNewItemChange, validationErrors]
  );

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={isMdDown ? 'sm' : 'md'}
        fullWidth
        fullScreen={isXsDown}
        PaperProps={{ component: 'form', onSubmit: handleSubmit }}
        sx={{
          '& .MuiDialog-paper': isXsDown
            ? {
                width: '100%',
                height: '100%',
                margin: 0,
                borderRadius: 0,
              }
            : isMdDown
              ? {
                  width: '90vw',
                  maxWidth: 600,
                  m: 0, // remove default margins
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 0,
                }
              : {
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  minWidth: 400,
                  maxWidth: 700,
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 0,
                },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6'>{title}</Typography>
            <IconButton onClick={handleClose} size='small'>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {Object.entries(schema).map(([key, fieldSchema]) => renderField(key, fieldSchema))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button type='submit' variant='contained' disabled={!isFormValid}>
            <LockIconWrapper right={`${resourceType}:create`}>Hinzufügen</LockIconWrapper>
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for unsaved changes */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelClose}
        maxWidth='sm'
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: 24,
          },
        }}
      >
        <DialogTitle>
          <Typography variant='h6'>Ungespeicherte Änderungen</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>Ungespeicherte Änderungen gehen verloren. Wirklich schließen?</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button onClick={handleCancelClose}>Abbrechen</Button>
          <Button onClick={handleConfirmClose} variant='contained' color='error'>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default AddItemDialog;
